/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import { PayloadAction } from "@reduxjs/toolkit";
import {
  IAccessControlSettings,
  IAdminState,
  IConfigureServer,
  IDataPollProps,
  IEmailSelectedGroup,
  IGeoRegion,
  INotificationGrpData,
  IPasswordSettings,
  IProfile,
  ISmtpEmail,
  ISnmpAgent,
  ISnmpGroup,
  ISyslogForward,
  ISystemProperties,
  ITrapReceive,
  IUserFileMgmt,
  ISyncOptions,
  ISshCliAccess,
  IsnmpEmailGroups,
  IThresholdCrossingSettingProfile,
  IThresholdCrossingSettingAction,
  IThresholdCrossingSettingSchedule,
  IThresholdCrossingSettingRule
} from "./types";
import { IKeyValue } from "../../../components/Administrations/types";
import { IGROUPS, ISiteGroup } from "../devices/types";
import { LOCAL_STORAGE } from "../../../globals/globals";

export const setIsCreateUserAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isCreateUser = action.payload;
};
export const setShowBannerAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.showBanner = action.payload;
};
export const setSelectedProfileAction = (state: IAdminState, action: PayloadAction<IProfile>) => {
  state.selectedProfile = action.payload;
};
export const setSelectedGeoRegionAction = (state: IAdminState, action: PayloadAction<IGeoRegion>) => {
  state.selectedGeoRegion = action.payload;
};
export const setSelectedGeoRegionIdsAction = (state: IAdminState, action: PayloadAction<string[]>) => {
  state.selectedGeoRegionIds = action.payload;
  localStorage.setItem(LOCAL_STORAGE.SELECTED_GEO_REGION_IDS, JSON.stringify(action.payload));
};
export const setSelectedGeoRegionsAction = (state: IAdminState, action: PayloadAction<IGeoRegion[]>) => {
  state.selectedGeoRegions = action.payload;
};
export const setCurrentSiteGroupsAction = (state: IAdminState, action: PayloadAction<ISiteGroup[]>) => {
  state.currentSiteGroups = [...action.payload].sort((a, b) => a.name.localeCompare(b.name));
};
export const setSelectedTaskTypeAction = (state: IAdminState, action: PayloadAction<string>) => {
  state.selectedTaskType = action.payload;
};
export const setSeverityValueAction = (state: IAdminState, action: PayloadAction<IKeyValue>) => {
  if (Object.values(action.payload).length > 0) {
    state.severityValue = { ...state.severityValue, ...action.payload };
  } else {
    state.severityValue = {};
  }
};

export const setSNMPValueAction = (state: IAdminState, action: PayloadAction<IKeyValue>) => {
  if (Object.values(action.payload).length > 0) {
    state.snmpValue = { ...state.snmpValue, ...action.payload };
  } else {
    state.snmpValue = {};
  }
};

export const setEmailValueAction = (state: IAdminState, action: PayloadAction<IKeyValue>) => {
  if (Object.values(action.payload).length > 0) {
    state.emailValue = { ...state.emailValue, ...action.payload };
  } else {
    state.emailValue = {};
  }
};

export const setIsCreateSnmpGroupAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isCreateSNMPGroup = action.payload;
};

export const setSelectedSnmpGroupAction = (state: IAdminState, action: PayloadAction<ISnmpGroup>) => {
  state.selectedSnmpGroup = action.payload;
};

export const setSelectedSnmpAgentAction = (state: IAdminState, action: PayloadAction<ISnmpAgent>) => {
  state.snmpAgent = action.payload;
};

export const setIsCreateEmailGroupAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isCreateEmailGroup = action.payload;
};

export const setSelectedEmailGroupAction = (state: IAdminState, action: PayloadAction<IEmailSelectedGroup>) => {
  state.selectedEmailGroup = action.payload;
};

export const setSelectedSmtpEmailAction = (state: IAdminState, action: PayloadAction<ISmtpEmail>) => {
  state.smtpEmail = action.payload;
};

export const setSystemPropertiesAction = (state: IAdminState, action: PayloadAction<ISystemProperties>) => {
  state.systemProperties = { ...state.systemProperties, ...action.payload };
};

export const setAccessControlSettingsAction = (state: IAdminState, action: PayloadAction<IAccessControlSettings>) => {
  state.accessControlSettings = { ...state.accessControlSettings, ...action.payload };
};

export const setPasswordSettingsAction = (state: IAdminState, action: PayloadAction<IPasswordSettings>) => {
  state.passwordSettings = { ...state.passwordSettings, ...action.payload };
};

export const setSelectedTrapReceiveAgentAction = (state: IAdminState, action: PayloadAction<ITrapReceive>) => {
  state.selectedTrapReceive = action.payload;
};

export const setGroupListAction = (state: IAdminState, action: PayloadAction<IGROUPS[]>) => {
  if (action.payload.length > 0) {
    const groupList = action.payload
      ?.map((item: IGROUPS) => ({ text: item?.name, value: item?.key ?? item.name, deviceCount: item?.deviceCount ?? 0 }))
      .sort((a, b) => (a.text || "").localeCompare(b.text || "")) || [];
    state.groupList = [{ text: "System Default", value: "NONE" }, ...groupList];
  } else {
    state.groupList = [{ text: "System Default", value: "NONE" }];
  }
};

export const setNotiGroupAction = (state: IAdminState, action: PayloadAction<INotificationGrpData>) => {
  state.selectedNotificationGroup = { ...state.selectedNotificationGroup, ...action.payload };
}

export const setSNMPEmailGroupsAction = (state: IAdminState, action: PayloadAction<IsnmpEmailGroups>) => {
  let snmpGroupsData = action.payload.snmpGroups || [];
  snmpGroupsData = snmpGroupsData.filter((item: string) => item.toLowerCase() !== "none");
  if (snmpGroupsData.length > 0) {
    state.snmpEmailGroups = { emailGroups: action.payload.emailGroups, snmpGroups: snmpGroupsData };
  } else {
    state.snmpEmailGroups = action.payload;
  }
};

export const setSelectedUserFileMgmtAction = (state: IAdminState, action: PayloadAction<IUserFileMgmt>) => {
  state.selectedUserFileMgmt = action.payload;
};

export const setIsAddNewUserFileMgmtAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isAddNewUserFileMgmt = action.payload;
};

export const setSelectedSyslogReceiverAction = (state: IAdminState, action: PayloadAction<ISyslogForward>) => {
  state.selectedSyslogReceiver = action.payload;
};

export const setServerConfigAction = (state: IAdminState, action: PayloadAction<IConfigureServer>) => {
  state.serverConfig = action.payload;
};

export const setSelectedBulkFileAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.selectedBulkFile = action.payload;
};

export const setFailedBulkUplaodAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.validatedOfflineErrors = action.payload;
};

export const setSuccessBulkUplaodAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.successOfflineUploads = action.payload;
};

export const setDataPollingAction = (state: IAdminState, action: PayloadAction<IDataPollProps>) => {
  state.dataPollerProperties = action.payload;
};

export const setStartDateAction = (state: IAdminState, action: PayloadAction<number>) => {
  state.startDate = action.payload;
};

export const setThresholdCrossingSettingProfileModelOpenModeAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.thresholdCrossingSettingProfileModelOpenMode = action.payload;
};

export const setThresholdCrossingSettingActionModelOpenModeAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.thresholdCrossingSettingActionModelOpenMode = action.payload;
};

export const setThresholdCrossingSettingScheduleModelOpenModeAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.thresholdCrossingSettingScheduleModelOpenMode = action.payload;
};

export const setThresholdCrossingSettingRuleModelOpenModeAction = (state: IAdminState, action: PayloadAction<any>) => {
  state.thresholdCrossingSettingRuleModelOpenMode = action.payload;
};

export const setSelectedThresholdCrossingSettingProfileAction = (state: IAdminState, action: PayloadAction<IThresholdCrossingSettingProfile>) => {
  state.selectedThresholdCrossingSettingProfile = action.payload;
};

export const setSelectedThresholdCrossingSettingActions = (state: IAdminState, action: PayloadAction<IThresholdCrossingSettingAction>) => {
  state.selectedThresholdCrossingSettingAction = action.payload;
};

export const setSelectedThresholdCrossingSettingScheduleAction = (state: IAdminState, action: PayloadAction<IThresholdCrossingSettingSchedule>) => {
  state.selectedThresholdCrossingSettingSchedule = action.payload;
};

export const setSelectedThresholdCrossingSettingRuleAction = (state: IAdminState, action: PayloadAction<IThresholdCrossingSettingRule>) => {
  state.selectedThresholdCrossingSettingRule = action.payload;
};

export const setIsAddEditDeleteThresholdCrossingSettingProfileAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isThresholdCrossingSettingProfileCrud = action.payload;
};

export const setIsAddEditDeleteThresholdCrossingSettingActions = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isThresholdCrossingSettingActionCrud = action.payload;
};

export const setIsAddEditDeleteThresholdCrossingSettingScheduleAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isThresholdCrossingSettingScheduleCrud = action.payload;
};

export const setIsAddEditDeleteThresholdCrossingSettingRuleAction = (state: IAdminState, action: PayloadAction<boolean>) => {
  state.isThresholdCrossingSettingRuleCrud = action.payload;
};


